/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type React from 'react';
import { useMemo } from 'react';
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import { getEdition, PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { JIRA_PRODUCT_DISCOVERY } from '@jira/platform__application-editions/src/application.tsx';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import { fg } from '@atlassian/jira-feature-gating';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import QueueIcon from '@atlaskit/icon/core/pages';
import ProjectIcon from '@atlaskit/icon/core/project';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	FAVORITE_TYPE_FILTER_MAP,
	FAVORITE_TYPE_FILTER_MESSAGE,
	FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM,
} from '../../constants.tsx';
import type { favoriteTypeFilter } from '../../types.tsx';

export const FILTER_MENU_ITEMS: FilterMenuItemData[] = [
	{
		Icon: AlignTextLeftIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.ALL,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.ALL],
	},
	{
		Icon: BoardIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.BOARD,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.BOARD],
	},
	{
		Icon: FilterIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.FILTER,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.FILTER],
	},
	{
		Icon: DashboardIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.DASHBOARD,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.DASHBOARD],
	},
	{
		Icon: QueueIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.QUEUE,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.QUEUE],
	},
	{
		Icon: SpacesIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.PROJECT,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.PROJECT],
	},
	{
		Icon: RoadmapIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.PLAN,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.PLAN],
	},
	{
		Icon: PlanIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION,
		message: FAVORITE_TYPE_FILTER_MESSAGE[FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION],
	},
];

export const FILTER_MENU_ITEMS_WITH_PROJECT_TERM: FilterMenuItemData[] = [
	{
		Icon: AlignTextLeftIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.ALL,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.ALL],
	},
	{
		Icon: BoardIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.BOARD,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.BOARD],
	},
	{
		Icon: FilterIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.FILTER,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.FILTER],
	},
	{
		Icon: DashboardIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.DASHBOARD,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.DASHBOARD],
	},
	{
		Icon: QueueIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.QUEUE,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.QUEUE],
	},
	{
		Icon: ProjectIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.PROJECT,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.PROJECT],
	},
	{
		Icon: RoadmapIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.PLAN,
		message: FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.PLAN],
	},
	{
		Icon: PlanIcon,
		filterType: FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION,
		message:
			FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM[FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION],
	},
];

export type FilterMenuItemData = {
	message: MessageDescriptor;
	Icon: React.ElementType;
	filterType: favoriteTypeFilter;
};

export const useFilterMenuItems = (): FilterMenuItemData[] => {
	const appEditions = useAppEditions();
	const jpdEdition =
		appEditions && fg('jpd-enable-roadmaps-recents-fe')
			? getEdition(JIRA_PRODUCT_DISCOVERY, appEditions)
			: undefined;
	const licenseStates = useLicenseStates();

	const isJSMLicensed = licenseStates?.serviceDesk === LICENSE_STATE_ACTIVE;

	const isJSWLicensed = licenseStates?.software === LICENSE_STATE_ACTIVE;
	const isJSWPremium = appEditions?.software === PREMIUM_EDITION;
	const isJPDPremium = fg('jpd-enable-roadmaps-recents-fe')
		? jpdEdition === PREMIUM_EDITION
		: false;

	return useMemo(
		() =>
			(expVal('project-terminology-refresh', 'isEnabled', false)
				? FILTER_MENU_ITEMS
				: FILTER_MENU_ITEMS_WITH_PROJECT_TERM
			).filter(({ filterType }) => {
				switch (filterType) {
					case FAVORITE_TYPE_FILTER_MAP.QUEUE:
						return isJSMLicensed;
					case FAVORITE_TYPE_FILTER_MAP.BOARD:
						return isJSWLicensed;
					case FAVORITE_TYPE_FILTER_MAP.PLAN:
						return isJSWPremium;
					case FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION:
						return isJPDPremium;
					default:
						return true;
				}
			}),
		[isJSMLicensed, isJSWLicensed, isJSWPremium, isJPDPremium],
	);
};
